/**
 * Audit-trail / prior-state recovery component (T037, US3 — FR-007 / SC-004).
 * Loads a document's events (newest first) and shows who/when/source per change, plus the recoverable
 * prior state from the latest event's `before`.
 */
import { useEffect, useState } from 'react';
import {
  formatTimestamp,
  getEntityHistory,
  type EntityHistory,
  type HistoryCollection,
} from '../services/history';

export function AuditTrail({
  collection,
  documentId,
  refreshKey,
}: {
  collection: HistoryCollection;
  documentId: string;
  /** Change this to force a reload (e.g. after an edit). */
  refreshKey?: number;
}) {
  const [history, setHistory] = useState<EntityHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEntityHistory({ collection, document_id: documentId })
      .then((h) => {
        if (!cancelled) setHistory(h);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [collection, documentId, refreshKey]);

  if (loading) return <p style={{ color: '#888' }}>Loading history…</p>;
  if (error) return <p style={{ color: 'crimson' }}>History error: {error}</p>;
  if (!history) return null;

  return (
    <section style={box}>
      <h3>Audit trail ({history.events.length})</h3>
      {history.events.length === 0 && <p style={{ color: '#888' }}>No events yet.</p>}
      <ol style={{ margin: 0, paddingLeft: 18 }}>
        {history.events.map((e) => (
          <li key={e.event_id} style={{ marginBottom: 6 }}>
            <code>{formatTimestamp(e.timestamp)}</code> · <strong>{e.actor}</strong> ·{' '}
            <em>{e.source}</em>
            {e.before === null && <span style={{ color: '#56b870' }}> · created</span>}
          </li>
        ))}
      </ol>
      {history.prior_state && (
        <details style={{ marginTop: 8 }}>
          <summary>Recoverable prior state (SC-004)</summary>
          <pre style={pre}>{JSON.stringify(history.prior_state, null, 2)}</pre>
        </details>
      )}
    </section>
  );
}

const box: React.CSSProperties = { border: '1px solid #ddd', borderRadius: 4, padding: 12, marginTop: 16 };
const pre: React.CSSProperties = {
  background: '#f6f8fa',
  padding: 8,
  borderRadius: 4,
  overflowX: 'auto',
  fontSize: 12,
};
